import { useState, FormEvent, useRef } from 'react';
import './ContactForm.css';

interface ContactFormProps {
  onSubmitted?: () => void;
}

export default function ContactForm({ onSubmitted }: ContactFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const formRef = useRef<HTMLElement>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage(null);

    try {
      const apiUrl = import.meta.env.VITE_FEEDBACK_API_URL || 'http://localhost:3001';
      const apiToken = import.meta.env.VITE_FEEDBACK_API_TOKEN;

      if (!apiToken) {
        throw new Error('API token not configured');
      }

      // Фронтенд всегда обращается к /api/feedback/submit
      // Nginx должен правильно проксировать /lapi/api на /api в бэкенде
      const response = await fetch(`${apiUrl}/api/feedback/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Token': apiToken
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Ошибка при отправке формы');
      }

      setMessage({
        type: 'success',
        text: data.message || 'Заявка отправлена. Скоро с вами свяжемся.'
      });
      
      setFormData({ name: '', email: '', company: '', message: '' });
      onSubmitted?.();
    } catch (error) {
      console.error('Form submission error:', error);
      setMessage({
        type: 'error',
        text: error instanceof Error 
          ? error.message 
          : 'Произошла ошибка при отправке. Пожалуйста, попробуйте еще раз или напишите нам на info@recvi.ru'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="contact-form-section" ref={formRef} data-scroll-section>
      <div className="container">
        <h2 className="section-title">Свяжитесь с нами</h2>
        <p className="section-subtitle">Оставьте контакты — мы расскажем подробнее о системе</p>
        
        <form onSubmit={handleSubmit} className="contact-form">
          <div className="form-group">
            <label htmlFor="name">Ваше имя</label>
            <input
              type="text"
              id="name"
              name="name"
              required
              placeholder="Иван Иванов"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              required
              placeholder="ivan@company.ru"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label htmlFor="company">Название компании</label>
            <input
              type="text"
              id="company"
              name="company"
              placeholder="ООО «Стройка»"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label htmlFor="message">Сообщение (необязательно)</label>
            <textarea
              id="message"
              name="message"
              rows={4}
              placeholder="Расскажите о ваших объектах или задайте вопрос..."
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            />
          </div>

          <button type="submit" className="submit-btn" disabled={isSubmitting}>
            <span className="btn-text">{isSubmitting ? 'Отправка...' : 'Отправить заявку'}</span>
            {isSubmitting && <span className="btn-loader"></span>}
          </button>

          {message && (
            <div className={`form-message ${message.type}`}>
              {message.text}
            </div>
          )}
        </form>
      </div>
    </section>
  );
}
